// @spec IAM-STR-031
// TanStack Query hooks for impersonation API

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useImpersonationStore } from '../stores/impersonation-store';
import type {
  ImpersonationEndResponse,
  ImpersonationStartRequest,
  ImpersonationStartResponse,
  ImpersonationValidateResponse,
} from '../types';

import { createApiClient } from '@/lib/api-client';

/**
 * Hook to start an impersonation session.
 *
 * Requires Super Admin role. On success, stores the impersonation
 * token and target user info in the impersonation store.
 *
 * @example
 * const { mutate: startImpersonation, isPending } = useStartImpersonation();
 * startImpersonation(
 *   { targetUserId: 'uuid', reason: 'Support ticket #123' },
 *   { onSuccess: () => navigate('/') }
 * );
 */
export function useStartImpersonation() {
  const { getToken } = useAuth();
  const setImpersonation = useImpersonationStore.use.setImpersonation();

  return useMutation({
    mutationFn: async (request: ImpersonationStartRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const response = await apiClient.post<ImpersonationStartResponse>(
        '/admin/impersonation/start',
        request
      );
      return response;
    },
    onSuccess: (data, variables) => {
      setImpersonation({
        token: data.impersonationToken,
        targetUser: data.targetUser,
        reason: variables.reason,
        expiresAt: data.expiresAt,
      });
    },
  });
}

/**
 * Hook to end an impersonation session.
 *
 * Requires Super Admin role and active impersonation session.
 * On success, clears the impersonation store.
 *
 * @example
 * const { mutate: endImpersonation, isPending } = useEndImpersonation();
 * endImpersonation();
 */
export function useEndImpersonation() {
  const { getToken } = useAuth();
  const token = useImpersonationStore.use.token();
  const clearImpersonation = useImpersonationStore.use.clearImpersonation();

  return useMutation({
    mutationFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const headers: Record<string, string> = {};
      if (token) {
        headers['X-Impersonation-Token'] = token;
      }

      const response = await apiClient.post<ImpersonationEndResponse>(
        '/admin/impersonation/end',
        undefined,
        { headers }
      );
      return response;
    },
    onSuccess: () => {
      clearImpersonation();
    },
    onError: () => {
      // Clear state even on error to prevent stuck state
      clearImpersonation();
    },
  });
}

/**
 * Hook to validate the current impersonation token.
 *
 * Used on app mount to verify any existing impersonation session.
 * If token is invalid or expired, clears the impersonation store.
 *
 * @example
 * const { data, isLoading } = useValidateImpersonation();
 * if (data?.valid) {
 *   // Show impersonation banner
 * }
 */
export function useValidateImpersonation() {
  const { getToken, isSignedIn } = useAuth();
  const token = useImpersonationStore.use.token();
  const clearImpersonation = useImpersonationStore.use.clearImpersonation();

  return useQuery({
    queryKey: ['admin', 'impersonation', 'validate', token],
    queryFn: async () => {
      if (!token) {
        return { valid: false } as ImpersonationValidateResponse;
      }

      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const response = await apiClient.get<ImpersonationValidateResponse>(
        '/admin/impersonation/validate',
        {
          headers: { 'X-Impersonation-Token': token },
        }
      );

      // If token is invalid, clear the store
      if (!response.valid) {
        clearImpersonation();
      }

      return response;
    },
    enabled: (isSignedIn ?? false) && !!token,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on failure, just clear state
  });
}

/**
 * Hook to check if impersonation is currently active.
 *
 * Returns true if there's a valid impersonation token in the store.
 *
 * @example
 * const isImpersonating = useIsImpersonating();
 * if (isImpersonating) {
 *   // Show impersonation UI
 * }
 */
export function useIsImpersonating(): boolean {
  const token = useImpersonationStore.use.token();
  const isExpired = useImpersonationStore.use.isExpired();
  return !!token && !isExpired();
}

/**
 * Hook to get the current impersonation target user.
 *
 * @example
 * const targetUser = useImpersonationTargetUser();
 * if (targetUser) {
 *   console.log(`Impersonating ${targetUser.email}`);
 * }
 */
export function useImpersonationTargetUser() {
  return useImpersonationStore.use.targetUser();
}
